
import { useState, useRef } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6jfk5ipaf6ievb71eig';

interface FormData {
  name: string;
  email: string;
  location: string;
  birth_type: string;
  story: string;
  consent: boolean;
}

function StoryModal({ onClose }: { onClose: () => void }) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    location: '',
    birth_type: '',
    story: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    setUploadedFiles((prev) => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, 3);
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) return;
    if (formData.story.length > 500) return;
    setIsSubmitting(true);
    setStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('location', formData.location);
      body.append('birth_type', formData.birth_type);
      body.append('story', formData.story);
      body.append('consent', formData.consent ? 'Yes' : 'No');
      body.append('photos', uploadedFiles.length > 0 ? `${uploadedFiles.length} photo(s) uploaded (Uncollectable)` : 'No photos uploaded');

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-3xl px-8 pt-8 pb-4 border-b border-amber-100 z-10">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600 text-lg"></i>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-amber-700 flex items-center justify-center">
              <i className="ri-quill-pen-line text-white text-lg"></i>
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900">Share Your Birth Story</h2>
          </div>
          <p className="text-gray-500 text-sm">
            Your story could inspire another mother. All submissions are reviewed with care before publishing.
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {status === 'success' ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 font-serif">Story Received!</h3>
              <p className="text-gray-600 leading-relaxed max-w-md mx-auto mb-6">
                Thank you for sharing your journey with us. We'll review your story with care and reach out to you at <strong>{formData.email}</strong> before publishing.
              </p>
              <button
                onClick={onClose}
                className="bg-amber-700 text-white px-8 py-3 rounded-full font-semibold hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="birth_story_form" className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Your Name <span className="text-amber-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="First name or preferred name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-sm transition-colors"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Email Address <span className="text-amber-600">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-sm transition-colors"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  City / Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g. Brooklyn, NY"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-sm transition-colors"
                />
              </div>

              {/* Birth Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Type of Birth <span className="text-amber-600">*</span>
                </label>
                <select
                  name="birth_type"
                  value={formData.birth_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-sm transition-colors bg-white cursor-pointer"
                >
                  <option value="">Select birth type...</option>
                  <option value="Vaginal Birth">Vaginal Birth</option>
                  <option value="C-Section">C-Section</option>
                  <option value="VBAC">VBAC (Vaginal Birth After Cesarean)</option>
                  <option value="Home Birth">Home Birth</option>
                  <option value="Birth Center">Birth Center</option>
                  <option value="Water Birth">Water Birth</option>
                  <option value="Unmedicated">Unmedicated Birth</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Story */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Your Story <span className="text-amber-600">*</span>
                </label>
                <textarea
                  name="story"
                  value={formData.story}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="Share your birth experience — what happened, how you felt, what you want other mothers to know..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-sm transition-colors resize-none"
                />
                <p className={`text-xs mt-1 ${formData.story.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                  {formData.story.length}/500 characters
                </p>
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Upload Photos <span className="text-gray-400 font-normal">(optional, up to 3)</span>
                </label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-400 hover:bg-amber-50/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files)}
                  />
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-image-add-line text-amber-700 text-xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {isDragging ? 'Drop your photos here' : 'Click or drag photos here'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP — max 3 photos</p>
                </div>

                {/* Preview */}
                {uploadedFiles.length > 0 && (
                  <div className="flex gap-3 mt-3 flex-wrap">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-amber-200 group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-cover object-top"
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                          className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <i className="ri-close-line text-white text-xs"></i>
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5">
                          <p className="text-white text-xs truncate">{file.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4">
                <input
                  type="checkbox"
                  id="consent"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                  className="mt-0.5 w-4 h-4 accent-amber-700 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="consent" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                  I give Lotus Maternal permission to publish my story on their blog and social media. I understand my story will be reviewed and may be lightly edited for clarity. My contact information will never be shared publicly.
                </label>
              </div>

              {status === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-red-700 text-sm font-semibold flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Something went wrong. Please try again or email us directly.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !formData.consent || formData.story.length > 500}
                className="w-full bg-amber-700 text-white font-bold py-4 rounded-xl hover:bg-amber-800 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Submitting...
                  </span>
                ) : (
                  'Submit My Story'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const ShareStoryCTA = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-700 flex items-center justify-center">
            <i className="ri-quill-pen-line text-3xl text-white"></i>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 mb-4 font-serif">
            Share Your Birth Story
          </h2>

          <p className="text-lg text-gray-700 mb-8 leading-relaxed max-w-2xl mx-auto">
            Your journey matters. Every birth story is unique, powerful, and deserves to be heard.
            Share your experience to inspire and support other mothers in our community.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => setShowModal(true)}
              className="bg-amber-700 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-amber-800 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Submit Your Story
            </button>

            <a
              href="mailto:info@lotusmaternal.com?subject=Birth Story Submission"
              className="bg-white text-amber-700 px-8 py-4 rounded-full text-base font-medium hover:bg-gray-50 transition-colors border-2 border-amber-700 whitespace-nowrap cursor-pointer"
            >
              Email Your Story
            </a>
          </div>

          <p className="text-sm text-gray-600 mt-6">
            All stories are reviewed with care and respect before publishing
          </p>
        </div>
      </section>

      {showModal && <StoryModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default ShareStoryCTA;
